import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useZuordnenBewerber, useZuordnungAufheben } from '../../api/bedarfe';
import { BewerberStatusBadge } from '../bewerber/BewerberStatusBadge';
import { SchulbegleiterSelect } from './SchulbegleiterSelect';
import { BewerberStatus } from '../../types/bewerber';
import type { Bedarf, ZuordnungKandidat } from '../../types/bedarf';

interface SchulbegleiterCardProps {
  bedarf: Bedarf;
}

/**
 * Who works this position — the jump from a Bedarf to its Bewerber, and the one
 * place where that is decided: the search field in the header assigns, the
 * button in the body releases.
 *
 * "Schulbegleiter", not "Zugeordnet", because the Bedarf header already calls
 * something else a Zuweisung — that one is the internal caseworker.
 */
export function SchulbegleiterCard({ bedarf }: SchulbegleiterCardProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const [ersatz, setErsatz] = useState<ZuordnungKandidat | null>(null);
  const aufheben = useZuordnungAufheben();
  const zuordnen = useZuordnenBewerber();
  const bewerber = bedarf.zugeordneterBewerber;

  const doZuordnen = async (kandidat: ZuordnungKandidat) => {
    const name = `${kandidat.firstName} ${kandidat.lastName}`;
    try {
      await zuordnen.mutateAsync({
        bedarfId: bedarf.id,
        bewerberId: kandidat.id,
        vorherBewerberId: bewerber?.id,
      });
      toast.success(`${name} dieser Stelle zugeordnet`);
      setErsatz(null);
    } catch (err: any) {
      // A 409 names the other Bedarf, which is exactly what the caseworker
      // needs to read — so the server's sentence is shown as it stands.
      const message = err?.response?.data?.message || 'Zuordnung fehlgeschlagen';
      toast.error(Array.isArray(message) ? message.join(', ') : message);
    }
  };

  // Replacing ends someone else's assignment, so it asks once — like the
  // release button does. A first assignment ends nothing and goes straight in.
  const handleSelect = (kandidat: ZuordnungKandidat) => {
    setIsConfirming(false);
    if (bewerber) {
      setErsatz(kandidat);
    } else {
      void doZuordnen(kandidat);
    }
  };

  const handleAufheben = async () => {
    try {
      await aufheben.mutateAsync({ bedarfId: bedarf.id, bewerberId: bewerber?.id });
      toast.success('Zuordnung aufgehoben');
      setIsConfirming(false);
    } catch {
      toast.error('Fehler beim Aufheben der Zuordnung');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex items-center justify-between gap-3">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Schulbegleiter</h3>
        <SchulbegleiterSelect
          bedarfId={bedarf.id}
          hasCurrent={!!bewerber}
          disabled={zuordnen.isPending || aufheben.isPending}
          onSelect={handleSelect}
        />
      </div>
      <div className="px-4 py-5 sm:p-6">
        {ersatz && bewerber && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p>
              {ersatz.firstName} {ersatz.lastName} statt {bewerber.firstName}{' '}
              {bewerber.lastName} zuordnen?
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={() => doZuordnen(ersatz)}
                disabled={zuordnen.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {zuordnen.isPending ? 'Wird zugeordnet…' : 'Ersetzen'}
              </button>
              <button
                type="button"
                onClick={() => setErsatz(null)}
                disabled={zuordnen.isPending}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
        {bewerber ? (
          <div className="space-y-3">
            <div>
              <Link
                to={`/bewerber/${bewerber.id}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                {bewerber.firstName} {bewerber.lastName}
              </Link>
              <div className="mt-1">
                <BewerberStatusBadge status={bewerber.status as BewerberStatus} />
              </div>
            </div>

            {(bewerber.email || bewerber.phone) && (
              <dl className="space-y-1 text-sm">
                {bewerber.email && (
                  <div>
                    <dt className="sr-only">E-Mail</dt>
                    <dd>
                      <a
                        href={`mailto:${bewerber.email}`}
                        className="text-primary-600 hover:text-primary-500 break-all"
                      >
                        {bewerber.email}
                      </a>
                    </dd>
                  </div>
                )}
                {bewerber.phone && (
                  <div>
                    <dt className="sr-only">Telefon</dt>
                    <dd className="text-gray-900">{bewerber.phone}</dd>
                  </div>
                )}
              </dl>
            )}

            {bedarf.zugeordnetAt && (
              <p className="text-xs text-gray-500">
                Zugeordnet am {new Date(bedarf.zugeordnetAt).toLocaleDateString('de-DE')}
              </p>
            )}

            {/* Removing an assignment is cheap to undo but easy to hit by
                accident next to the contact details, so it asks once. */}
            {isConfirming ? (
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleAufheben}
                  disabled={aufheben.isPending}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {aufheben.isPending ? 'Wird aufgehoben…' : 'Wirklich aufheben'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsConfirming(false)}
                  disabled={aufheben.isPending}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Abbrechen
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setErsatz(null);
                  setIsConfirming(true);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Zuordnung aufheben
              </button>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500">
            <p>Noch niemand zugeordnet.</p>
            <p className="mt-1 text-xs">
              Über das Suchfeld oben rechts lässt sich jemand dieser Stelle zuordnen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
